/** One in-flight read, exponential quiet/error backoff, and no timer while the page is hidden. */
export function adaptivePoll(
  read: () => Promise<boolean>,
  { baseMs = 60_000, maxMs = 300_000, immediate = false } = {},
): () => void {
  let delay = baseMs;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  let stopped = false;
  const clear = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined; };
  const schedule = () => {
    clear();
    if (!stopped && !running && document.visibilityState !== 'hidden') timer = setTimeout(() => void tick(), delay);
  };
  const tick = async () => {
    clear();
    if (stopped || running || document.visibilityState === 'hidden') return;
    running = true;
    try { delay = await read() ? baseMs : Math.min(maxMs, delay * 2); }
    catch { delay = Math.min(maxMs, delay * 2); }
    finally { running = false; schedule(); }
  };
  const visibility = () => {
    clear();
    // Resume after the base delay: repeated visibility events never trigger a request burst.
    if (document.visibilityState !== 'hidden') { delay = baseMs; schedule(); }
  };
  document.addEventListener('visibilitychange', visibility);
  if (immediate) void tick(); else schedule();
  return () => { stopped = true; clear(); document.removeEventListener('visibilitychange', visibility); };
}
